import { Injectable } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, Observable, throwError } from "rxjs";
import { catchError, filter, map, take, tap } from "rxjs/operators";
import { ConfigInterface } from "@core/interfaces/entities/config-interface";
import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { isMaintenanceResponse, redirectToMaintenancePage, setMaintenanceModeActive } from "@core/utils/maintenance-mode.util";

@Injectable({
    providedIn: "root",
})
export class ConfigService {
    private rawHttp: HttpClient;
    private root = "/api/utils";
    private config$ = new BehaviorSubject<ConfigInterface | null>(null);

    constructor(private handler: HttpBackend) {
        this.rawHttp = new HttpClient(handler);
    }

    getConfigsAPI(): Observable<ConfigInterface> {
        const headers = new HttpHeaders({
            "NO-INTERCEPT": "true",
        });
        return this.rawHttp.get<ConfigInterface>(`${this.root}/env-config`, { headers });
    }

    populate(shouldRefresh = false): Observable<ConfigInterface> {
        if (!shouldRefresh && this.config$.getValue()) {
            return this.config$.asObservable() as Observable<ConfigInterface>;
        }
        return this.getConfigsAPI().pipe(
            tap((conf) => this.config$.next(conf)),
            map((conf) => conf),
            catchError((error) => {
                if (isMaintenanceResponse(error)) {
                    setMaintenanceModeActive();
                    redirectToMaintenancePage();
                }

                return throwError(() => error);
            }),
        );
    }

    getOnce<T>(selector: (conf: ConfigInterface) => T | null | undefined) {
        return this.populate().pipe(
            filter((conf) => !conf),
            map(selector),
            filter((v) => v !== null && v !== undefined),
            distinctUntilChanged(),
            take(1),
        );
    }

    getConfigSync(): ConfigInterface | null {
        return this.config$.getValue();
    }

    getOKTAConfig(): { url: string; clientId: string } | null {
        return this.getConfigSync()?.["okta"] ?? null;
    }

    getPCPConfigObs(): Observable<string> {
        return this.getOnce((conf) => conf.pcp.url);
    }
    getPCPConfig(): string {
        return this.getConfigSync()["pcp"]["url"];
    }

    getBlueButtonConfig(): { url: string } {
        return this.getConfigSync()["blueButton"];
    }

    getHraConfig(): { url: string } {
        return this.getConfigSync()["hra"];
    }

    getMemberPortalUrlConfig(): string {
        return this.getConfigSync()["memberPortalUrl"];
    }
}
